function parseQuizTagString(str) {
  return (str || '')
    .split(',')
    .map(t => t.trim())
    .filter(Boolean)
    .map(t => {
      const [rawName, rawWeight] = t.split(':');
      const name = (rawName || '').trim();
      const weight = parseFloat(rawWeight);
      return {
        key: name.toLowerCase(),
        weight: Number.isFinite(weight) ? weight : 1,
        isCount: name.toLowerCase() === 'count',
      };
    })
    .filter(t => t.key);
}

if (!customElements.get('quiz-section')) {
  customElements.define(
    'quiz-section',
    class QuizSection extends HTMLElement {
      connectedCallback() {
        this._key = `wt-quiz-${this.dataset.sectionId}`;
        this.slides = Array.from(this.querySelectorAll('quiz-slide'));

        // Tag each slide with a CSS class based on its content's data-block-type
        this.slides.forEach(slide => {
          const type = this._getSlideType(slide);
          if (type) slide.classList.add(`wt-quiz__slide--${type}`);
        });
        const questionSlides = this.slides.filter(s => this._isQuestionSlide(s));
        if (questionSlides.length) questionSlides.at(-1).classList.add('wt-quiz__slide--last-question');
        this._progressFill = this.querySelector('.wt-quiz__progress-fill');
        this._progressText = this.querySelector('.wt-quiz__progress-text');

        this._state = { currentIndex: 0, tagMap: {}, history: [] };
        // A new page load always starts a fresh quiz instead of restoring a
        // previously completed result from localStorage.
        try { localStorage.removeItem(this._key); } catch (_) {}
        this._activate(0, false);

        this._onAnswer = this._handleAnswer.bind(this);
        this._onNewsletterSubmit = this._handleNewsletterSubmit.bind(this);

        this.addEventListener('quiz:answer', this._onAnswer);

        this.addEventListener('click', e => {
          if (e.target.closest('.wt-quiz__nav-next:not([disabled])')) {
            const nextBtn = e.target.closest('.wt-quiz__nav-next');
            if (nextBtn?.dataset.redirectUrl) { this._completeAndRedirect(nextBtn.dataset.redirectUrl); return; }
            this._advance();
            return;
          }
          if (e.target.closest('.wt-quiz__nav-prev')) { this._back(); return; }
          if (e.target.closest('.wt-quiz__retake')) { e.preventDefault(); this._retake(); return; }
          if (e.target.closest('.wt-quiz__newsletter-skip')) { e.preventDefault(); this._advance(); return; }
          const newsletterSubmitBtn = e.target.closest('.wt-newsletter__send');
          if (newsletterSubmitBtn) {
            e.preventDefault();
            newsletterSubmitBtn.closest('form')?.requestSubmit();
            return;
          }
          if (e.target.closest('.hero__button')) { e.preventDefault(); this._advance(); return; }
        });

        const newsletterForm = this.querySelector('.wt-quiz__newsletter-form');
        if (newsletterForm) newsletterForm.addEventListener('submit', this._onNewsletterSubmit);
      }

      disconnectedCallback() {
        clearTimeout(this._autoAdvanceTimer);
        this.removeEventListener('quiz:answer', this._onAnswer);
      }

      _activate(index, _animate = true) {
        this.slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
        });
        this._state.currentIndex = index;
        this._syncProgress(index);
        this._syncNavButtons(index);

        if (_animate) {
          const firstOption = this.slides[index]?.querySelector('quiz-option');
          if (firstOption) {
            requestAnimationFrame(() => firstOption.focus({ preventScroll: true }));
          }
        }
      }

      _syncNavButtons(index) {
        const slide = this.slides[index];
        if (!slide) return;
        const nextBtn = slide.querySelector('.wt-quiz__nav-next');
        if (!nextBtn) return;

        const options = slide.querySelector('.wt-quiz__options');
        const maxSelect = parseInt(options?.dataset.maxSelect || '1', 10) || 1;
        nextBtn.hidden = maxSelect <= 1;
        const hasSelection = !!slide.querySelector('quiz-option.is-selected');
        nextBtn.disabled = !hasSelection;

        const questionSlides = this.slides.filter(
          s => this._isQuestionSlide(s)
        );
        const qi = questionSlides.indexOf(slide);
        if (qi === -1) return;

        const prevBtn = slide.querySelector('.wt-quiz__nav-prev');
        if (prevBtn) prevBtn.hidden = qi === 0;

        const isLast = qi === questionSlides.length - 1;
        const label = nextBtn.querySelector('.wt-quiz__nav-label');
        if (label) {
          label.textContent = isLast
            ? (nextBtn.dataset.resultLabel || label.textContent)
            : (nextBtn.dataset.nextLabel || label.textContent);
        }
      }

      _syncProgress(index) {
        const questionSlides = this.slides.filter(
          s => this._isQuestionSlide(s)
        );
        const current = this.slides[index];
        const isQuestion = this._isQuestionSlide(current);

        const progressEl = this.querySelector('.wt-quiz__progress');
        if (progressEl) progressEl.hidden = !isQuestion;

        if (!isQuestion) return;

        const headerEl = this.querySelector('.wt-quiz__header');
        if (headerEl) {
          const styles = getComputedStyle(current);
          headerEl.style.backgroundColor = styles.getPropertyValue('--quiz-block-bg').trim() || '';
          headerEl.style.color = styles.getPropertyValue('--quiz-block-text').trim() || '';
        }

        const qi = questionSlides.indexOf(current) + 1;
        const total = questionSlides.length;
        if (this._progressFill) this._progressFill.style.width = `${(qi / total) * 100}%`;
        if (this._progressText) this._progressText.textContent = `${qi} / ${total}`;
      }

      _applyTags(tagString, selected) {
        parseQuizTagString(tagString).forEach(t => {
          if (t.isCount) {
            if (selected) {
              this._state.resultCount = t.weight;
            } else if (this._state.resultCount === t.weight) {
              delete this._state.resultCount;
            }
            return;
          }
          if (selected) {
            this._state.tagMap[t.key] = (this._state.tagMap[t.key] || 0) + t.weight;
          } else {
            const newVal = (this._state.tagMap[t.key] || 0) - t.weight;
            if (newVal > 0.0001) {
              this._state.tagMap[t.key] = newVal;
            } else {
              delete this._state.tagMap[t.key];
            }
          }
        });
      }

      _handleAnswer(e) {
        this._applyTags(e.detail.tags, e.detail.selected);
        this._save();
        const currentSlide = this.slides[this._state.currentIndex];
        const nextBtn = currentSlide?.querySelector('.wt-quiz__nav-next');
        if (nextBtn) {
          const hasSelection = !!currentSlide.querySelector('quiz-option.is-selected');
          nextBtn.disabled = !hasSelection;
          const questionSlides = this.slides.filter(
            s => this._isQuestionSlide(s)
          );
          const qi = questionSlides.indexOf(currentSlide);
          const isLast = qi !== -1 && qi === questionSlides.length - 1;
          if (isLast) {
            const url = this._getRedirectUrl();
            if (url) nextBtn.dataset.redirectUrl = url;
          }
        }

        const options = e.target.closest('.wt-quiz__options');
        const maxSelect = parseInt(options?.dataset.maxSelect || '1', 10) || 1;
        const selectedCount = options?.querySelectorAll('quiz-option.is-selected').length || 0;
        clearTimeout(this._autoAdvanceTimer);
        if (e.detail.selected && (maxSelect <= 1 || selectedCount >= maxSelect)) {
          if (nextBtn) nextBtn.hidden = true;
          const answeredIndex = this._state.currentIndex;
          // Single-choice questions and multi-choice questions at their limit
          // advance after the selected state has had time to register visually.
          this._autoAdvanceTimer = setTimeout(() => {
            if (this._state.currentIndex !== answeredIndex) return;
            if (!e.target.classList.contains('is-selected')) return;
            const redirectUrl = nextBtn?.dataset.redirectUrl;
            if (redirectUrl) {
              this._completeAndRedirect(redirectUrl);
            } else {
              this._advance();
            }
          }, 600);
        }
      }

      _handleNewsletterSubmit(e) {
        e.preventDefault();
        const form = e.target;
        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          redirect: 'manual',
        })
          .catch(() => {})
          .finally(() => this._advance());
      }

      _findSlideIndexByQuestionKey(key) {
        if (!key) return -1;
        let el;
        try {
          el = this.querySelector(`[data-question-key="${CSS.escape(key)}"]`);
        } catch (_) {
          el = Array.from(this.querySelectorAll('[data-question-key]')).find(n => n.dataset.questionKey === key);
        }
        if (!el) return -1;
        const slide = el.closest('quiz-slide');
        return slide ? this.slides.indexOf(slide) : -1;
      }

      _nextIndex() {
        const currentSlide = this.slides[this._state.currentIndex];
        let next = this._state.currentIndex + 1;
        if (!currentSlide) return next;

        const jumpTargets = Array.from(currentSlide.querySelectorAll('quiz-option.is-selected'))
          .map(o => o.dataset.jumpTo)
          .filter(Boolean);

        if (jumpTargets.length) {
          const targetIdx = this._findSlideIndexByQuestionKey(jumpTargets[jumpTargets.length - 1]);
          if (targetIdx >= 0) next = targetIdx;
        }
        return next;
      }

      _advance() {
        const next = this._nextIndex();
        if (next >= this.slides.length) return;
        if (!this._state.history) this._state.history = [];
        this._state.history.push(this._state.currentIndex);
        if (next === this._resultsIndex()) {
          this._complete();
        }
        this._activate(next);
      }

      _complete() {
        this._calcResult();
        this._state.completedAt = new Date().toISOString();
        this._state.submissionId = this._newSubmissionId();
        this._save();
        this._submitResult();
      }

      async _completeAndRedirect(url) {
        this._complete();
        const pending = this._submitPromise || Promise.resolve();
        await Promise.race([
          pending.catch(() => {}),
          new Promise(resolve => setTimeout(resolve, 1500)),
        ]);
        window.location.href = url;
      }

      _newSubmissionId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      }

      _collectAnswers() {
        return this.slides
          .filter(slide => this._isQuestionSlide(slide))
          .map(slide => {
            const questionEl = slide.querySelector('[data-block-type="question"]');
            const answers = Array.from(slide.querySelectorAll('quiz-option.is-selected'))
              .map(option => option.dataset.answer || option.querySelector('.wt-quiz__option-heading')?.textContent?.trim())
              .filter(Boolean);
            return {
              question_key: questionEl?.dataset.questionKey || null,
              question: questionEl?.dataset.question || questionEl?.querySelector('.wt-quiz__question-heading')?.textContent?.trim() || '',
              answers,
            };
          })
          .filter(item => item.answers.length);
      }

      _submitResult() {
        if (!this.dataset.customerId || !this.dataset.webhookUrl || !this._state.submissionId) return;
        const payload = {
          submission_id: this._state.submissionId,
          quiz_section_id: this.dataset.sectionId || null,
          quiz_title: this.dataset.quizTitle || null,
          completed_at: this._state.completedAt,
          answers: this._collectAnswers(),
          result_handles: this._state.resultHandles || [],
          tag_scores: this._state.tagMap || {},
        };
        this._submitPromise = fetch(this.dataset.webhookUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload),
        }).then(response => {
          if (!response.ok) throw new Error(`Quiz result save failed (${response.status})`);
          return response.json();
        }).catch(error => {
          console.warn(error);
          return null;
        });
      }

      _back() {
        const history = this._state.history;
        if (history && history.length) {
          this._activate(history.pop());
          return;
        }
        const prev = this._state.currentIndex - 1;
        if (prev >= 0) this._activate(prev);
      }

      _retake() {
        const firstQuestionIndex = this.slides.findIndex(s => this._isQuestionSlide(s));
        const startIndex = firstQuestionIndex >= 0 ? firstQuestionIndex : 0;
        this._state = { currentIndex: startIndex, tagMap: {}, history: [] };
        clearTimeout(this._autoAdvanceTimer);
        try { localStorage.removeItem(this._key); } catch (_) {}
        this.querySelectorAll('quiz-option').forEach(o => o.classList.remove('is-selected'));
        if (this._progressFill) {
          this._progressFill.style.transition = 'none';
          this._progressFill.style.width = '0%';
          requestAnimationFrame(() => { this._progressFill.style.transition = ''; });
        }
        this._activate(startIndex);
      }

      _getSlideType(slide) {
        return slide.querySelector('[data-block-type]')?.dataset.blockType || null;
      }

      _isQuestionSlide(slide) {
        const t = this._getSlideType(slide);
        return t === 'question';
      }

      _resultsIndex() {
        return this.slides.findIndex(s => this._getSlideType(s) === 'results');
      }

      _sortedTags() {
        return Object.entries(this._state.tagMap)
          .filter(([, score]) => score > 0)
          .sort(([, a], [, b]) => b - a);
      }

      _getRedirectUrl() {
        const resultsEl = this.querySelector('.wt-quiz__results');
        if (resultsEl?.dataset.redirect !== 'true') return null;
        const topTag = this._sortedTags()[0]?.[0];
        if (!topTag) return null;
        const match = this.querySelector(`[data-result-tag="${topTag}"]`);
        return match?.dataset.url || null;
      }

      _calcResult() {
        const items = Array.from(this.querySelectorAll('[data-result-tag]'));
        items.forEach(item => { item.hidden = true; });
        const fallback = this.querySelector('.wt-quiz__result-fallback');
        const infoEl = this.querySelector('.wt-quiz__result-info');

        const sortedTags = this._sortedTags();
        const wantCount = this._state.resultCount || 1;
        const n = Math.max(1, Math.min(wantCount, items.length || 1));

        const chosen = [];
        for (const [tag] of sortedTags) {
          if (chosen.length >= n) break;
          const match = items.find(item => item.dataset.resultTag === tag && !chosen.includes(item));
          if (match) chosen.push(match);
        }

        const first = chosen[0];
        if (infoEl) {
          const preRendered = first?.querySelector('.wt-quiz__result-item-info');
          if (preRendered) infoEl.innerHTML = preRendered.innerHTML;
          infoEl.hidden = !first;
          infoEl.style.setProperty('--quiz-result-info-bg', first?.dataset.infoBg || '');
          infoEl.style.setProperty('--quiz-result-info-text', first?.dataset.infoText || '');
        }

        if (chosen.length) {
          chosen.forEach(item => {
            item.hidden = false;
            this._refreshSlider(item);
          });
          this._state.resultHandles = chosen.map(item => item.dataset.resultTag);
          this._state.resultHandle = this._state.resultHandles[0];
          if (fallback) fallback.hidden = true;
        } else {
          if (fallback) fallback.hidden = false;
        }
      }

      _renderSavedResult() {
        const handles = this._state.resultHandles
          || (this._state.resultHandle ? [this._state.resultHandle] : []);
        if (!handles.length) return;

        const items = Array.from(this.querySelectorAll('[data-result-tag]'));
        const chosen = handles
          .map(h => items.find(i => i.dataset.resultTag === h))
          .filter(Boolean);

        if (!chosen.length) {
          const fallback = this.querySelector('.wt-quiz__result-fallback');
          if (fallback) fallback.hidden = false;
          return;
        }

        chosen.forEach(item => {
          item.hidden = false;
          this._refreshSlider(item);
        });

        const infoEl = this.querySelector('.wt-quiz__result-info');
        if (infoEl) {
          const first = chosen[0];
          const preRendered = first.querySelector('.wt-quiz__result-item-info');
          if (preRendered) {
            infoEl.innerHTML = preRendered.innerHTML;
            infoEl.hidden = false;
            infoEl.style.setProperty('--quiz-result-info-bg', first.dataset.infoBg || '');
            infoEl.style.setProperty('--quiz-result-info-text', first.dataset.infoText || '');
          }
        }
      }

      _refreshSlider(container) {
        const slideshow = container?.querySelector('slideshow-section');
        if (!slideshow) return;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (slideshow.swiper) {
            slideshow.swiper.destroy(true, true);
            slideshow.swiper = null;
          }
          if (typeof slideshow.swiperInitilize === 'function') {
            slideshow.swiperInitilize();
          }
        }));
      }

      _save() {
        try {
          localStorage.setItem(this._key, JSON.stringify(this._state));
        } catch (_) {}
      }
    }
  );
}

if (!customElements.get('quiz-slide')) {
  customElements.define('quiz-slide', class QuizSlide extends HTMLElement {});
}

if (!customElements.get('quiz-option')) {
  customElements.define(
    'quiz-option',
    class QuizOption extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
        this._onClick = this._handleClick.bind(this);
        this._onKeydown = (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._handleClick(); }
        };
        this.addEventListener('click', this._onClick);
        this.addEventListener('keydown', this._onKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('keydown', this._onKeydown);
      }

      _dispatchAnswer(selected) {
        this.dispatchEvent(
          new CustomEvent('quiz:answer', {
            bubbles: true,
            detail: { tags: this.dataset.tags || '', selected },
          })
        );
      }

      _handleClick() {
        const options = this.closest('.wt-quiz__options');
        const maxSelect = parseInt(options?.dataset.maxSelect || '1', 10) || 1;
        const isSelected = this.classList.contains('is-selected');

        if (isSelected) {
          this.classList.remove('is-selected');
          this._dispatchAnswer(false);
          return;
        }

        if (maxSelect <= 1) {
          const prev = options?.querySelector('quiz-option.is-selected');
          if (prev && prev !== this) {
            prev.classList.remove('is-selected');
            prev._dispatchAnswer?.(false);
          }
        } else {
          const selectedCount = options ? options.querySelectorAll('quiz-option.is-selected').length : 0;
          if (selectedCount >= maxSelect) return;
        }

        this.classList.add('is-selected');
        this._dispatchAnswer(true);
      }
    }
  );
}
